import type { ConversationId } from "@redotech/effect-api-shared/ids/definitions";
import {
  type ConversationListItemResponse,
  type ConversationStatus,
} from "@redotech/effect-api-shared/models/shopper-ai/conversation/conversation";
import { reactQueryClient } from "@redotech/redo-web/rpc-query/react-query-config";
import { useQueryClient } from "@tanstack/react-query";
import { memo } from "react";
import { draftQueryKeys } from "../db/collections";
import { conversationCountsQueryKey } from "../hooks/use-conversation-counts";
import { useGlobalConversationCollections } from "../providers/global-conversation-collections-context";
import { useTeamNotificationStream } from "./team-notification-stream-provider";

/**
 * NotificationSyncHandlers
 *
 * Registers built-in sync handlers for messages, conversations, threads, and presence.
 * This component subscribes to notification events and syncs them to TanStack DB collections.
 *
 * Must be placed AFTER:
 * - TeamNotificationStreamProvider (provides the stream)
 * - GlobalConversationStoreProvider (provides the collections)
 *
 * Handles:
 * - messageCreated: Clear SENDING draft for the thread, insert real message
 * - conversationCreated: Insert new conversations
 * - conversationUpdated: Update conversation status, assignment, priority, read status
 * - threadCreated: Insert new threads
 * - presenceViewing: Track users currently viewing a conversation
 * - typing: Track users currently typing in a conversation
 * - presenceCleared: Remove or update presence when a user leaves or stops typing
 */
export const NotificationSyncHandlers = memo(
  function NotificationSyncHandlers() {
    const collections = useGlobalConversationCollections();
    const queryClient = useQueryClient();

    // Message created handler
    useTeamNotificationStream("messageCreated", (event) => {
      // Remove the SENDING draft from both layers:
      // 1. writeDelete removes it from TanStack DB's in-memory store immediately
      // 2. removeQueries wipes the stale React Query backing cache so useLiveQuery
      //    doesn't serve the initial-save snapshot when MessageInput remounts
      for (const draft of collections.drafts.values()) {
        if (
          String(draft.conversationId) === String(event.conversationId) &&
          draft.status === "SENDING"
        ) {
          collections.drafts.utils.writeDelete(String(draft.id));
        }
      }
      queryClient.removeQueries({ queryKey: draftQueryKeys.all });
      void queryClient.invalidateQueries({ queryKey: draftQueryKeys.all });

      if (!collections.messages.isReady()) {
        return;
      }

      collections.messages.utils.writeInsert({
        ...event.message,
        sentAt: new Date(event.message.sentAt as unknown as string),
        conversationId: event.conversationId,
      });
    });

    // Conversation created handler
    useTeamNotificationStream("conversationCreated", (event) => {
      if (collections.conversationsList.isReady()) {
        collections.conversationsList.utils.writeInsert(
          event.conversation as any,
        );
      }

      setTimeout(() => {
        void reactQueryClient.invalidateQueries({
          queryKey: conversationCountsQueryKey,
        });
        void reactQueryClient.invalidateQueries({
          queryKey: ["activeViewStatusCounts"],
        });
      }, 2000);
    });

    // Conversation updated handler
    useTeamNotificationStream("conversationUpdated", (event) => {
      if (collections.conversationsList.isReady()) {
        let update: Partial<ConversationListItemResponse> & {
          id: ConversationId;
        } = { id: event.conversationId };

        switch (event.updateType) {
          case "status_changed":
            update = {
              ...update,
              ...(event.newStatus !== undefined && {
                status: event.newStatus as ConversationStatus,
              }),
            };
            break;
          case "assigned":
            update = { ...update, mongoAssigneeId: event.newAssignee };
            break;
          case "priority_changed":
            update = { ...update, priority: event.newPriority };
            break;
          case "read":
            update = { ...update, readAt: new Date() };
            break;
          case "tag_added":
          case "tag_removed":
            // Tags are handled separately - might need to refetch
            break;
        }

        collections.conversationsList.utils.writeUpdate(update);
        setTimeout(() => {
          void reactQueryClient.invalidateQueries({
            queryKey: conversationCountsQueryKey,
          });
          void reactQueryClient.invalidateQueries({
            queryKey: ["activeViewStatusCounts"],
          });
        }, 2000);
      }

      let update: Partial<ConversationListItemResponse> & {
        id: ConversationId;
      } = { id: event.conversationId };

      switch (event.updateType) {
        case "status_changed":
          update = {
            ...update,
            ...(event.newStatus !== undefined && {
              status: event.newStatus as ConversationStatus,
            }),
            ...(event.newSnoozedUntil !== undefined && {
              snoozedUntil: event.newSnoozedUntil,
            }),
          };
          break;
        case "assigned":
          update = { ...update, mongoAssigneeId: event.newAssignee };
          break;
        case "priority_changed":
          update = { ...update, priority: event.newPriority };
          break;
        case "read":
          update = { ...update, readAt: new Date() };
          break;
        case "tag_added":
        case "tag_removed":
          // Tags are handled separately - might need to refetch
          break;
      }

      collections.conversationsList.utils.writeUpdate(update);
    });

    // Thread created handler
    useTeamNotificationStream("threadCreated", (event) => {
      if (!collections.threads.isReady()) {
        return;
      }

      collections.threads.utils.writeInsert({ ...event.thread });
    });

    // Presence viewing handler — user opened a conversation
    useTeamNotificationStream("presenceViewing", (event) => {
      if (!collections.presence.isReady) {
        return;
      }

      collections.presence.utils.writeUpsert({
        id: `${event.conversationId}:${event.userId}`,
        conversationId: event.conversationId,
        userId: event.userId,
        isViewing: true,
        isTyping: false,
      });
    });

    // Typing handler — user is actively typing
    useTeamNotificationStream("typing", (event) => {
      if (!collections.presence.isReady) {
        return;
      }

      collections.presence.utils.writeUpsert({
        id: `${event.conversationId}:${event.userId}`,
        conversationId: event.conversationId,
        userId: event.userId,
        isViewing: true,
        isTyping: true,
      });
    });

    // Presence cleared handler — user left or stopped typing
    useTeamNotificationStream("presenceCleared", (event) => {
      if (!collections.presence.isReady) {
        return;
      }

      const key = `${event.conversationId}:${event.userId}`;

      if (event.activity === "typing") {
        // User stopped typing but is still viewing
        collections.presence.utils.writeUpsert({
          id: key,
          conversationId: event.conversationId,
          userId: event.userId,
          isViewing: true,
          isTyping: false,
        });
      } else {
        // activity === "viewing" or "all": user left the conversation
        collections.presence.utils.writeDelete(key);
      }
    });

    return null;
  },
);
